// Custom Components
import Page from "../Page";
import { useState, useEffect } from "react";
import { 
  Card, 
  Stack, 
  Box, 
  Typography, 
  Chip, 
  Grid, 
  Paper,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { BaseTable } from "../BaseTable/BaseTable";
import { teamsColumns } from "./teams.constants";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList } from "src/hooks/useFetch.hook";
import { TeamUserModal } from "./TeamUserModal";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { useDispatch, useSelector } from "react-redux";
import { FEATURE } from "src/util/enums";
import { useNavigate } from "react-router-dom";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.customShadows.z8,
  }
}));


export const TeamList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const hasCreateAccess = useAccess(FEATURE.TEAM, "C");
  const isReadOnly = useAccess(FEATURE.TEAM, "R");
  const { data: teams, isFetching } = useList("teams");
  const { open, data } = useSelector(({ dialog }) => dialog);
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [filteredCount, setFilteredCount] = useState(0);
  const handleClose = () => {
    dispatch(dialogSlice.actions.setDialog(false));
  };
  const rowOrCellClickRedirect = ({ id,row }) => {
    if(isReadOnly) return;
    dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
      detailsId:row?.name,
      tabOpenedFor:"team",
      tabOpened:"general"
    }))
    navigate(`/team/${id}`);
  };

  useEffect(() => {
    if (!isFetching && teams && Array.isArray(teams)){
      let data = [...teams];
      setTableData(data);
      setFilteredCount(data.length);
    }
  }, [isFetching, teams]);

  // Calculate statistics from current table data (filtered data)
  const currentData = tableData || (teams && Array.isArray(teams) ? teams : []);
  const totalTeams = currentData.length;
  const activeTeams = currentData.filter(team => team.active?.toLowerCase() === "y")?.length || 0;
  const inactiveTeams = currentData.filter(team => team.active?.toLowerCase() === "n")?.length || 0;

  const handleSearching = (event) => {
    const dataToSearchFrom = teams && Array.isArray(teams) ? teams : [];
    const searchValue = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchValue);
    
    if (searchValue) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "name",
            "active",
            "description",
            "creationTime",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "creationTime"
                ? yearFirstDateTimeFormat(value).toString()
                : value === "Y" ? "active" : value === "N" ? "inactive" : value ?  value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchValue)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
      setFilteredCount(filteredDataArr.length);
    } else {
      setTableData([...(teams && Array.isArray(teams) ? teams : [])]);
      setFilteredCount(teams && Array.isArray(teams) ? teams.length : 0);
    }
  };

  return (
    <Page
      {...(hasCreateAccess && { path: `/team/new` })}
      actionButtonText="Add Team"
    >
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* <Typography variant="h4" sx={{ 
          fontWeight: 600, 
          color: (theme) => theme.palette.primary.main,
          mb: 1
        }}>
          Teams Management
        </Typography> */}
        <Typography variant="body1" sx={{ 
          color: (theme) => theme.palette.text.secondary,
          mb: 3
        }}>
          Manage and organize your teams and their members
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.primary.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.primary.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:people-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.primary.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Teams
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.primary.main 
              }}>
                {totalTeams}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.success.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.success.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:checkmark-circle-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.success.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Active Teams
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.success.main 
              }}>
                {activeTeams}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.error.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.error.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:close-circle-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.error.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Inactive Teams
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.error.main 
              }}>
                {inactiveTeams}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Teams
          </Typography>
          {searchValue && (
            <Chip
              label={`${filteredCount} of ${teams && Array.isArray(teams) ? teams.length : 0} teams`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      {/* Combined Search and Table Card */}
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search by team name, status, description, or creation date..."
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={() => window.location.reload()}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData || []}
          columns={teamsColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row.teamId}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          showExportCsvBtn={false}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.primary.lighter,
              borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            },
            '& .MuiDataGrid-columnHeader': {
              fontWeight: 600,
              color: (theme) => theme.palette.primary.main,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
              }
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: (theme) => theme.palette.action.hover,
            }
          }}
        />
      </StyledListCard>
      
      {open && (
        <TeamUserModal open={open} teamId={data} onClose={handleClose} />
      )}
    </Page>
  );
};
